import { useEffect, useRef, useState } from 'react';
import {
	Card,
	Form,
	Input,
	Tabs,
	Button,
	InputNumber,
	notification,
	Typography,
	Alert,
	Divider,
	Select,
	TourStepProps,
	Spin,
	Switch,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { getResellerData } from '@/store/reseller/selector';
import {
	createConnectAccount,
	createPlan,
	getConnectAccount,
	getSubscriptionUnitPrice,
	updateTutorialStepStatus,
	updateTourStatus,
} from '@/api/reseller';
import { openModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';
import ResellerDashboard from '../ResellerDashboard';
import { ResellerSiderItem } from 'src/enums/IResellerSiderItems';
import FeatureGatingCustomizeModal from '../Reseller_Common_Components/FeatureGatingCustomizeModal';
import MeterRateConfig from './DropDownComponent';
import SMSPricingConfig from './SMSPricingConfig';
import {
	CheckCircleFilled,
	DollarOutlined,
	SettingOutlined,
	MinusCircleOutlined,
	PlusOutlined,
	PlusCircleOutlined,
} from '@ant-design/icons';
import { BookOpenIcon, CreditCardIcon, SettingsIcon } from 'lucide-react';
import TutorialsModal from '../TutorialsModal';
import { Convert } from 'easy-currencies';

import '../ResellerDashboard.scss';
import TourButton from '@/components/TourButton/TourButton';
import { setTour } from '@/store/resellerTour/slice';
import { AppRoutes } from 'src/enums/ERoutes';
import { getResellerTourState } from '@/store/resellerTour/selector';
import ResellerTour from '../ResellerTour/ResellerTour';
import { currencyOptions } from '@/constants/unitPrice';
import { getSMSPricingInfo } from '@/api/smsPricing';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface PlanFormValues {
	name: string;
	monthlyPrice: number;
	currency: string;
	freeMinutes: number;
	freeSMSCredits: number;
	featureDescriptions?: string[];
	smsZonePrices: Record<string, number>;
	meterPrices: Record<string, number>;
	isTrial: boolean;
	trialDays: number;
	trialMinutes: number;
	trialSMSCredits: number;
}

interface Features {
	featuresSubAccount?: Record<string, any>;
}

const tutorialItems = [
	{
		icon: (
			<div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
				<CreditCardIcon className="text-blue-500" size={20} />
			</div>
		),
		title: 'Creating a plan',
		description: 'Creating a plan with Stripe connected in My AI Frontdesk',
		url: 'https://www.myaifrontdesk.com/reseller-tutorials/creating-a-plan-with-stripe-connected-in-my-ai-frontdesk',
	},
	{
		icon: (
			<div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
				<SettingsIcon className="text-blue-500" size={20} />
			</div>
		),
		title: 'Connecting stripe to My AI Frontdesk',
		description: 'Guide on connecting My AI Frontdesk with Stripe',
		url: 'https://www.myaifrontdesk.com/reseller-tutorials/guide-on-connecting-my-ai-frontdesk-with-stripe',
	},
];

// Feature Preview Component
const FeaturePreview = ({ features }: { features?: string[] }) => {
	if (!features || features.length === 0 || features[0] === undefined) {
		return <Text type="secondary">No features added yet</Text>;
	}

	return (
		<div className="space-y-2">
			{features.map((feature, index) => (
				<div key={index} className="flex items-center gap-2">
					<div>•</div>
					<Text>{feature}</Text>
				</div>
			))}
		</div>
	);
};

// Plan Features Form Component
const PlanFeaturesForm = () => {
	return (
		<Form.List name="featureDescriptions" initialValue={['']}>
			{(fields, { add, remove }) => (
				<div className="space-y-4">
					<div className="flex justify-between items-center">
						<Text strong>Add Features</Text>
						<Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
							Add Feature
						</Button>
					</div>

					{fields.map((field) => (
						<Form.Item key={field.key} validateTrigger={['onChange', 'onBlur']} className="mb-2">
							<div className="flex gap-2">
								<Form.Item {...field} noStyle>
									<Input placeholder="Enter feature description" className="flex-1" />
								</Form.Item>
								{fields?.length && (
									<div className="!flex !flex-row !justify-center !items-center">
										<Button
											type="text"
											onClick={() => add()}
											icon={<PlusCircleOutlined size={50} className="!text-[1.08rem]" />}
											className="text-green-500"
										/>

										<Button
											type="text"
											icon={<MinusCircleOutlined />}
											onClick={() => remove(field.name)}
											className="text-red-500 "
										/>
									</div>
								)}
							</div>
						</Form.Item>
					))}
				</div>
			)}
		</Form.List>
	);
};

const ResellerPlansCreation = () => {
	const [features, setFeatures] = useState<Features | undefined>(undefined);
	const [selectedCountry, setSelectedCountry] = useState<string>('US');
	const resellerData = useAppSelector(getResellerData);
	const [form] = Form.useForm<PlanFormValues>();
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();
	const [isTutorialsModalOpen, setIsTutorialsModalOpen] = useState(false);
	const [selectedCurrency, setSelectedCurrency] = useState('usd');
	const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({});
	const [defaultValues, setDefaultValues] = useState<Partial<PlanFormValues>>({
		trialDays: 1,
		trialMinutes: 10,
		trialSMSCredits: 10,
		freeSMSCredits: 0,
		isTrial: false,
		currency: 'usd',
	});

	const planCreationTourRef1 = useRef<HTMLDivElement>(null);
	const planCreationTourRef2 = useRef<HTMLDivElement>(null);

	const supportedCountries = [
		{ value: 'US', label: 'United States' },
		{ value: 'AU', label: 'Australia' },
		{ value: 'CA', label: 'Canada' },
		{ value: 'GB', label: 'United Kingdom' },
	];

	const planCreationTourSteps: TourStepProps[] = [
		{
			title: <h1 className="font-bold text-2xl">Charge your clients outside of the platform</h1>,
			description: (
				<p className="text-base">
					You can always charge your clients manually outside of the platform anyway you like.
				</p>
			),
			target: planCreationTourRef1.current as HTMLElement,
			placement: 'right',
		},
		{
			title: <h1 className="font-bold text-2xl">Bill your clients using Stripe</h1>,
			description: (
				<p className="text-base">
					You can fully automate how you charge clients using our stripe integration. Create plans
					and minutely charges
				</p>
			),
			target: planCreationTourRef2.current as HTMLElement,
			placement: 'left',
		},
	];

	// Fetch SMS pricing data from the API
	const {
		data: smsPricingData,
		isLoading: isSmsPricingLoading,
		error: smsPricingError,
	} = useQuery({
		queryKey: ['getSMSPricingInfo'],
		queryFn: () => getSMSPricingInfo({ clientType: 'RESELLER' }),
	});

	const createConnectAccountMutation = useMutation({
		mutationFn: createConnectAccount,
		onSuccess: (data) => {
			if (data.url) {
				window.location.href = data.url;
			}
		},
		onError: () => {
			notification.error({ message: 'Error', description: 'Failed to create connect account' });
		},
	});

	const createPlanMutation = useMutation({
		mutationKey: ['createPlan'],
		mutationFn: createPlan,
		onSuccess: () => {
			notification.success({ message: 'Plan created successfully' });
			form.resetFields();
			queryClient.invalidateQueries({ queryKey: ['getMeter'] });
		},
		onError: (error: Error) => {
			notification.error({
				message: 'Error',
				description: error.message || 'Failed to create plan',
			});
		},
	});

	const updateTutorialMutation = useMutation({
		mutationFn: updateTutorialStepStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['reseller'] });
		},
	});

	const updateToursMutation = useMutation({
		mutationFn: updateTourStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['reseller'] });
		},
	});

	const resellerTour = useAppSelector(getResellerTourState);

	useEffect(() => {
		if (!resellerData?.emailAddress || resellerTour.open) {
			return;
		}

		if (!resellerData?.tours?.plan_creation) {
			updateToursMutation.mutate('plan_creation');
			dispatch(
				setTour({
					open: true,
					currentStep: 0,
					returnTo: AppRoutes.RESELLER_PLAN_CREATION,
				}),
			);
		}
	}, [resellerData?.emailAddress, resellerTour.open]);

	const { data: connectAccount } = useQuery({
		queryKey: ['getConnectAccount'],
		queryFn: getConnectAccount,
		enabled: !!resellerData?.stripe_connect_id,
	});

	const { data: subscriptionUnitPrice } = useQuery({
		queryKey: ['getSubscriptionUnitPrice'],
		queryFn: getSubscriptionUnitPrice,
	});

	// Initialize form with default values
	useEffect(() => {
		// Initialize meter prices with default values of 0
		const defaultMeterPrices = {
			deepgram: 0,
			cartesia: 0,
			'11labs': 0,
			transferCall: 0,
		};

		// Initialize SMS zone prices if data is available
		if (smsPricingData?.data?.zones) {
			const initialSmsValues = smsPricingData.data.zones.reduce(
				(acc: Record<string, number>, zone: any) => {
					// Use the base price from the API as the default value
					acc[zone.id] = zone.basePrice || 0;
					return acc;
				},
				{},
			);

			const formDefaults = {
				meterPrices: defaultMeterPrices,
				smsZonePrices: initialSmsValues,
				isTrial: false,
				trialDays: 1,
				trialMinutes: 10,
				trialSMSCredits: 10,
			};

			form.setFieldsValue(formDefaults as any);
			setDefaultValues(formDefaults);
		} else {
			const formDefaults = {
				meterPrices: defaultMeterPrices,
				isTrial: false,
				trialDays: 1,
				trialMinutes: 10,
				trialSMSCredits: 10,
			};

			form.setFieldsValue(formDefaults as any);
			setDefaultValues(formDefaults);
		}
	}, [smsPricingData, form]);

	const handleCreatePlan = async (values: PlanFormValues) => {
		try {
			if (!resellerData?.stripe_connect_id) {
				notification.error({
					message: 'Error',
					description: 'Stripe connect ID is missing',
				});
				return;
			}

			// Check if SMS pricing data is loaded
			if (!smsPricingData?.data?.zones) {
				notification.error({
					message: 'Error',
					description: 'SMS pricing information is not loaded yet. Please wait and try again.',
				});
				return;
			}

			// Filter out empty feature descriptions
			const featureDescriptions =
				values.featureDescriptions?.filter((desc) => desc?.trim() !== '' && desc !== undefined) ||
				[];

			// Get SMS pricing from form values and clean up null/undefined values
			const rawSmsPricing = values.smsZonePrices || {};

			// Process SMS pricing to handle null/empty values
			// If a zone doesn't have a value set by the user, use the base price from the API
			const smsZonePrices = smsPricingData.data.zones.reduce(
				(acc: Record<string, number>, zone: any) => {
					const userValue = rawSmsPricing[zone.id];
					// If user hasn't set a value, use the base price from the API
					acc[zone.id] =
						userValue === null || userValue === undefined ? zone.basePrice || 0 : Number(userValue);
					return acc;
				},
				{},
			);

			if (values.isTrial) {
				if (values.trialDays > 30) {
					notification.error({
						message: 'Error',
						description: 'Trial days cannot be greater than 30',
					});
					return;
				}
				if (values.trialDays < 1 || values.trialMinutes < 1 || values.trialSMSCredits < 1) {
					notification.error({
						message: 'Error',
						description: 'Trial days, minutes and SMS credits cannot be less than 1',
					});
					return;
				}
			}

			const payload = {
				email: resellerData?.emailAddress,
				name: values.name,
				monthlyPrice: values.monthlyPrice,
				currency: values.currency,
				freeMinutes: values.freeMinutes,
				freeSMSCredits: values.freeSMSCredits || 0,
				trialCredits: values.isTrial ? values.trialMinutes || 0 : 0,
				trialDays: values.isTrial ? values.trialDays || 0 : 0,
				trialSMSCredits: values.isTrial ? values.trialSMSCredits || 0 : 0,
				product: {
					productName: values.name,
					active: true,
					description: `${values.name} Plan`,
					metadata: {
						type: 'voice-plan',
						application: 'ai-receptionist',
					},
				},
				voice: {
					voiceName: `${values.name} Voice Usage`,
					voiceDescription: 'Voice Usage Metering',
					voiceMetadata: {
						type: 'voice-item',
						application: 'ai-receptionist',
					},
				},
				featuresSubAccount: {
					...features?.featuresSubAccount,
				},
				descriptions: featureDescriptions,
				smsZonePrices,
				meterPrices: values.meterPrices || {
					deepgram: 0,
					cartesia: 0,
					'11labs': 0,
					transferCall: 0,
				},
			};

			// Create the plan
			createPlanMutation.mutate(payload);
		} catch (error) {
			notification.error({
				message: 'Error',
				description: error instanceof Error ? error.message : 'Failed to create plan',
			});
		}
	};

	const handleTutorialClick = () => {
		if (!resellerData?.tutorial_steps?.plan_creation_tutorial) {
			updateTutorialMutation.mutate('plan_creation_tutorial');
		}
		setIsTutorialsModalOpen(true);
	};

	const handleTourClick = () => {
		setIsTutorialsModalOpen(false);

		if (!resellerData?.tours?.plan_creation) {
			updateToursMutation.mutate('plan_creation');
		}
	};

	// Add function to fetch exchange rates
	const fetchExchangeRates = async () => {
		try {
			const convert = await Convert().from('USD').fetch();
			setExchangeRates(convert.rates);
		} catch (error) {
			console.error('Error fetching exchange rates :', error);
		}
	};

	// Fetch exchange rates on component mount
	useEffect(() => {
		fetchExchangeRates();
	}, []);

	// Function to convert price to USD
	const convertToUSD = (price: number, fromCurrency: string): number => {
		if (fromCurrency === 'usd') {
			return price;
		}
		const rate = exchangeRates[fromCurrency.toUpperCase()];
		return rate ? price / rate : price;
	};

	return (
		<ResellerDashboard activeKey={ResellerSiderItem.PLAN_CREATION}>
			<FeatureGatingCustomizeModal
				planFeatures={features}
				setPlanFeatures={(featuresObject: Record<string, any>) => setFeatures(featuresObject)}
			/>

			<div className="p-8">
				<div className="flex items-center justify-between mb-6">
					<div>
						<Title level={2} className="!mb-2">
							Plan Management
						</Title>
						<Text type="secondary">Create and manage your subscription plans</Text>
					</div>

					<div className="flex items-center gap-2">
						<Button
							type="default"
							size="large"
							className={
								!resellerData?.tutorial_steps?.plan_creation_tutorial
									? 'bg-[#F1F8FF] hover:bg-[#F1F8FF] text-blue-600 has-ping overflow-hidden'
									: ''
							}
							onClick={handleTutorialClick}
						>
							<BookOpenIcon size={16} />
							<span>Tutorials</span>
						</Button>
						{connectAccount?.connected && (
							<Alert
								message="Stripe Connected"
								type="success"
								showIcon
								icon={<CheckCircleFilled />}
								className="!m-0"
							/>
						)}
					</div>
				</div>

				{isSmsPricingLoading ? (
					<div className="flex flex-col items-center justify-center py-10">
						<Spin size="large" />
						<Text className="mt-3">Loading plan configuration data...</Text>
					</div>
				) : smsPricingError ? (
					<Alert
						message="Error Loading Configuration"
						description="There was an error loading the plan configuration data. Please refresh the page and try again."
						type="error"
						showIcon
					/>
				) : (
					<>
						{!connectAccount?.connected && (
							<Card className="mb-8">
								<Title level={3} className="!mb-2 text-center">
									How do you want to bill your customers?
								</Title>
								<Text type="secondary" className="block text-center mb-8">
									Choose the payment workflow that fits your business
								</Text>

								<div className="grid grid-cols-5 gap-8">
									{/* Manual Billing Card */}
									<Card
										ref={planCreationTourRef1}
										className="h-full col-span-2 border-[3px] border-gray-200 hover:border-gray-300 transition-all rounded-xl"
									>
										<div className="flex flex-col h-full">
											<div className="flex items-center gap-2 mb-6">
												<div className="p-2 rounded-lg bg-gray-100">
													<DollarOutlined className="text-2xl text-gray-600" />
												</div>
												<Title level={4} className="!mb-0">
													Manual Billing
												</Title>
											</div>

											<Text className="text-gray-600 mb-6">You handle all payments yourself.</Text>

											<ul className="space-y-4 mb-8">
												<li className="flex items-center gap-3">
													<MinusCircleOutlined className="text-gray-500" />
													<Text>Bill customers manually</Text>
												</li>
												<li className="flex items-center gap-3">
													<MinusCircleOutlined className="text-gray-500" />
													<Text>Keep track of their usage</Text>
												</li>
												<li className="flex items-center gap-3">
													<MinusCircleOutlined className="text-gray-500" />
													<Text>Handle subscription changes</Text>
												</li>
												<li className="flex items-center gap-3">
													<MinusCircleOutlined className="text-gray-500" />
													<Text>Manage payment collection</Text>
												</li>
											</ul>
										</div>
									</Card>

									{/* Stripe Billing Card */}
									<Card
										ref={planCreationTourRef2}
										className="h-full col-span-3 !border-[2px]  !border-blue-700 hover:shadow-[0_0_0_3px_rgba(29,78,216,0.25)] transition-all bg-blue-50 rounded-xl relative"
									>
										<div className="absolute -top-3 -right-3 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium">
											Recommended
										</div>
										<div className="flex flex-col h-full">
											<div className="flex items-center gap-2 mb-6">
												<div className="p-2 rounded-lg bg-blue-100">
													<DollarOutlined className="text-2xl text-blue-600" />
												</div>
												<Title level={4} className="!mb-0">
													Bill with Stripe
												</Title>
											</div>

											<Text className="text-gray-600 mb-6">
												Easier for you and your clients. Let us handle payments.
											</Text>

											<ul className="space-y-4 mb-8">
												<li className="flex items-center gap-3">
													<CheckCircleFilled className="text-blue-500 text-lg" />
													<Text>Automatically bill through your Stripe</Text>
												</li>
												<li className="flex items-center gap-3">
													<CheckCircleFilled className="text-blue-500 text-lg" />
													<Text>Let customers self-subscribe</Text>
												</li>
												<li className="flex items-center gap-3">
													<CheckCircleFilled className="text-blue-500 text-lg" />
													<Text>Track usage automatically</Text>
												</li>
												<li className="flex items-center gap-3">
													<CheckCircleFilled className="text-blue-500 text-lg" />
													<Text>Handle upgrades/downgrades</Text>
												</li>
												<li className="flex items-center gap-3">
													<CheckCircleFilled className="text-blue-500 text-lg" />
													<Text>Provide registration links for each plan</Text>
												</li>
											</ul>

											<div className="mt-auto pt-6 border-t border-blue-200">
												<Title level={5} className="!mb-2">
													Want to automate billing?
												</Title>
												<Text type="secondary" className="block mb-4">
													{!connectAccount?.accountId
														? 'Set up your Stripe account to start receiving payments and managing subscriptions.'
														: 'Complete your Stripe onboarding to activate payment processing.'}
												</Text>
												<div className="flex items-center gap-4">
													<Select
														value={selectedCountry}
														onChange={(value) => setSelectedCountry(value)}
														options={supportedCountries}
														style={{ width: '300px', height: '40px' }}
														size="large"
														placeholder="Select your country"
													/>
													<Button
														type="primary"
														size="large"
														icon={<DollarOutlined />}
														onClick={() =>
															createConnectAccountMutation.mutate({
																email: resellerData?.emailAddress as string,
																country: selectedCountry,
															})
														}
														disabled={createConnectAccountMutation.isPending || !selectedCountry}
														loading={createConnectAccountMutation.isPending}
														className="bg-blue-500 hover:bg-blue-600"
													>
														Connect Your Stripe
													</Button>
												</div>
											</div>
										</div>
									</Card>
								</div>
							</Card>
						)}

						{connectAccount?.connected && (
							<Card>
								<Form form={form} onFinish={handleCreatePlan} layout="vertical">
									<Alert
										message="Credit Pricing Information"
										description="1 credit = $0.12. This rate applies after all free credits are used up.
        For minutes: 300 free minute credits are provided per account.
        For SMS: 83 free SMS credits are provided per account (1 credit = 6 SMS messages).
        These credits are per account and not transferable between accounts.
        They are enough for around 98% of business use cases.
        "
										type="info"
										showIcon
										className="mb-6"
									/>
									<Tabs defaultActiveKey="basic">
										<TabPane
											tab={
												<span data-testid="basic-plan-tab" className="flex items-center gap-2">
													<span className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white font-bold text-base border-2 border-blue-500 mr-2">
														1
													</span>
													Basic Plan Details
												</span>
											}
											key="basic"
										>
											<div
												className="grid"
												style={{ gridTemplateColumns: '1.25fr 1fr', gap: '2rem' }}
											>
												<div className="p-6 border-2 border-gray-300 rounded-2xl shadow-2xl bg-gray-50">
													<Form.Item
														name="name"
														label={<span className="font-semibold text-gray-900">Plan Name</span>}
														rules={[{ required: true, message: 'Please enter plan name' }]}
													>
														<Input placeholder="e.g. Professional Plan" />
													</Form.Item>

													<Form.Item
														name="currency"
														label={<span className="font-semibold text-gray-900">Currency</span>}
														initialValue="usd"
														rules={[{ required: true, message: 'Please select currency' }]}
													>
														<Select
															options={currencyOptions}
															onChange={(value) => {
																setSelectedCurrency(value);
																form.setFieldsValue({ currency: value });
															}}
														/>
													</Form.Item>

													<Form.Item
														name="monthlyPrice"
														label={
															<span className="font-semibold text-gray-900">{`Monthly Price (${selectedCurrency.toUpperCase()})`}</span>
														}
														rules={[{ required: true, message: 'Please enter monthly price' }]}
													>
														<InputNumber
															prefix={
																currencyOptions.find((opt) => opt.value === selectedCurrency)
																	?.symbol || '$'
															}
															min={0}
															step={0.01}
															precision={2}
															style={{ width: '100%' }}
															placeholder="99.99"
														/>
													</Form.Item>

													<Form.Item
														name="freeMinutes"
														label={
															<div className="flex items-center gap-2">
																<span className="font-semibold text-gray-900">Free Minutes</span>
																<span className="text-xs text-gray-500">
																	(Each account includes 300 credits/month)
																</span>
															</div>
														}
														rules={[{ required: true, message: 'Please enter free credits' }]}
													>
														<InputNumber
															min={0}
															style={{ width: '100%' }}
															placeholder="100"
															data-testid="free-minutes-input"
														/>
													</Form.Item>
													<Form.Item
														name="freeSMSCredits"
														label={
															<div className="flex items-center gap-2">
																<span className="font-semibold text-gray-900">
																	Free SMS Credits
																</span>
																<span className="text-xs text-gray-500">
																	(Each account includes 83 credits/month)
																</span>
															</div>
														}
														rules={[{ required: true, message: 'Please enter free credits' }]}
													>
														<InputNumber min={0} style={{ width: '100%' }} placeholder="83" />
													</Form.Item>

													<Form.Item
														name="isTrial"
														label={<span className="font-semibold text-gray-900">Trial</span>}
														valuePropName="checked"
														initialValue={false}
													>
														<Switch />
													</Form.Item>

													<Form.Item
														noStyle
														shouldUpdate={(prevValues, currentValues) =>
															prevValues.isTrial !== currentValues.isTrial
														}
													>
														{({ getFieldValue }) => {
															const isTrial = getFieldValue('isTrial');
															return isTrial ? (
																<div className="space-y-4">
																	<Alert
																		message="Trial Accounts"
																		description={
																			resellerData?.unlimitedInboundAgents
																				? 'Trial accounts that obtain phone numbers will incur a $2/month charge for the reseller'
																				: 'Trial accounts that obtain phone numbers will incur a one-time $2 charge for the reseller'
																		}
																		type="info"
																		showIcon
																		className="mb-4"
																	/>
																	<Form.Item
																		name="trialDays"
																		label={
																			<span className="font-semibold text-gray-900">
																				Trial Days (Max 30)
																			</span>
																		}
																		initialValue={1}
																		rules={[
																			{
																				required: true,
																				message: 'Please enter at least 1 trial days',
																			},
																		]}
																	>
																		<InputNumber min={1} style={{ width: '100%' }} />
																	</Form.Item>
																	<Form.Item
																		name="trialMinutes"
																		label={
																			<span className="font-semibold text-gray-900">
																				Trial Minutes
																			</span>
																		}
																		initialValue={10}
																		rules={[
																			{
																				required: true,
																				message: 'Please enter at least 10 trial minutes',
																			},
																			{
																				type: 'number',
																				min: 1,
																				message: 'Trial minutes must be at least 1',
																			},
																		]}
																	>
																		<InputNumber min={1} style={{ width: '100%' }} />
																	</Form.Item>
																	<Form.Item
																		name="trialSMSCredits"
																		label={
																			<span className="font-semibold text-gray-900">
																				Trial SMS Credits
																			</span>
																		}
																		initialValue={10}
																		rules={[
																			{
																				required: true,
																				message: 'Please enter at least 10 trial SMS credits',
																			},
																			{
																				type: 'number',
																				min: 1,
																				message: 'Trial SMS credits must be at least 1',
																			},
																		]}
																	>
																		<InputNumber min={1} style={{ width: '100%' }} />
																	</Form.Item>
																</div>
															) : null;
														}}
													</Form.Item>

													<Divider />

													<Text
														strong
														className="block mb-4"
														data-testid="feature-descriptions-title"
													>
														Feature Descriptions (Optional)
													</Text>
													<Text className="block mb-4 text-gray-600">
														Add features that will be displayed to your customers
													</Text>
													<PlanFeaturesForm />
												</div>

												<div className="p-6 border-2 border-gray-300 rounded-2xl shadow-2xl bg-gray-50">
													<Title level={4}>Plan Preview</Title>
													<Form.Item
														dependencies={[
															'name',
															'monthlyPrice',
															'freeMinutes',
															'featureDescriptions',
															'currency',
															'isTrial',
															'trialDays',
															'trialMinutes',
															'trialSMSCredits',
														]}
													>
														{({ getFieldsValue }) => {
															const values = getFieldsValue();
															// Use form values or fall back to default values
															const displayValues = {
																name: values.name || 'Plan Name',
																monthlyPrice: values.monthlyPrice || 0,
																currency: values.currency || defaultValues.currency || 'usd',
																freeMinutes: values.freeMinutes || 0,
																isTrial:
																	values.isTrial !== undefined
																		? values.isTrial
																		: defaultValues.isTrial,
																trialDays:
																	values.trialDays !== undefined
																		? values.trialDays
																		: defaultValues.trialDays,
																trialMinutes:
																	values.trialMinutes !== undefined
																		? values.trialMinutes
																		: defaultValues.trialMinutes,
																trialSMSCredits:
																	values.trialSMSCredits !== undefined
																		? values.trialSMSCredits
																		: defaultValues.trialSMSCredits,
																featureDescriptions: values.featureDescriptions || [],
															};

															const basePrice = subscriptionUnitPrice?.unitPrice || 0;

															// Convert monthly price to USD for margin calculation
															const monthlyPriceInUSD = convertToUSD(
																displayValues.monthlyPrice,
																displayValues.currency,
															);
															const earnings = monthlyPriceInUSD - basePrice;
															const earningsPercentage =
																basePrice > 0 ? ((earnings / basePrice) * 100).toFixed(1) : '0';

															return (
																<Card className="bg-white border-2 border-gray-300 shadow">
																	<Title level={4}>{displayValues.name}</Title>
																	<div className="space-y-4">
																		<div>
																			<Text type="secondary">Monthly Price</Text>
																			<div className="text-xl font-semibold">
																				{currencyOptions.find(
																					(opt) => opt.value === displayValues.currency,
																				)?.symbol || '$'}
																				{displayValues.monthlyPrice.toFixed(2)}
																			</div>
																		</div>
																		<div>
																			<Text type="secondary">Price You Pay (USD)</Text>
																			<div className="text-lg text-gray-600">
																				${basePrice.toFixed(2)}
																			</div>
																		</div>
																		<div>
																			<Text type="secondary">
																				Price You Charge ({displayValues.currency?.toUpperCase()})
																			</Text>
																			<div
																				className={`text-lg ${earnings >= 0 ? 'text-green-600' : 'text-red-600'} font-semibold`}
																			>
																				{currencyOptions.find(
																					(opt) => opt.value === displayValues.currency,
																				)?.symbol || '$'}
																				{displayValues.monthlyPrice.toFixed(2)} (
																				{earningsPercentage}% margin)
																			</div>
																		</div>
																		<div>
																			<Text type="secondary">Free Minutes</Text>
																			<div className="text-lg">
																				{displayValues.freeMinutes} minutes
																			</div>
																		</div>

																		{displayValues.isTrial && (
																			<>
																				<div>
																					<Text type="secondary">Trial Days</Text>
																					<div className="text-lg">
																						{displayValues.trialDays} days
																					</div>
																				</div>
																				<div>
																					<Text type="secondary">Trial Minutes</Text>
																					<div className="text-lg">
																						{displayValues.trialMinutes} minutes
																					</div>
																				</div>
																				<div>
																					<Text type="secondary">Trial SMS Credits</Text>
																					<div className="text-lg">
																						{displayValues.trialSMSCredits} SMS credits
																					</div>
																				</div>
																			</>
																		)}
																		<div>
																			<Text type="secondary">Features</Text>
																			<div className="mt-2">
																				<FeaturePreview
																					features={displayValues.featureDescriptions?.filter(
																						(f: string) => f?.trim() !== '' && f !== undefined,
																					)}
																				/>
																			</div>
																		</div>
																	</div>
																</Card>
															);
														}}
													</Form.Item>
												</div>
											</div>
										</TabPane>

										<TabPane
											tab={
												<span
													data-testid="advanced-options-tab"
													className="flex items-center gap-2"
												>
													<span className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white font-bold text-base border-2 border-blue-500 mr-2">
														2
													</span>
													Advanced Options
												</span>
											}
											key="advanced"
										>
											<div className="space-y-8">
												<div className="p-6 border-2 border-gray-300 rounded-2xl shadow-2xl bg-gray-50">
													<Title level={4}>Voice Rates Configuration</Title>
													<MeterRateConfig
														form={form}
														currency={selectedCurrency}
														exchangeRates={exchangeRates}
													/>
												</div>

												<div className="p-6 border-2 border-gray-300 rounded-2xl shadow-2xl bg-gray-50">
													<Title level={4}>Feature Management</Title>
													<div className="space-y-4">
														<Text className="block mb-4">
															Customize the features available in this plan:
														</Text>
														<Button
															type="primary"
															icon={<SettingOutlined />}
															data-testid="customize-features-button"
															onClick={() =>
																dispatch(
																	openModal({
																		modalId: MODAL_IDS.FEATURE_GATING_CUSTOMIZE_MODAL,
																		data: {
																			isTrialForm: false,
																			planCreation: true,
																			featuresSubAccount: features?.featuresSubAccount,
																		},
																	}),
																)
															}
														>
															Customize Features
														</Button>
													</div>
												</div>
											</div>
										</TabPane>

										<TabPane
											tab={
												<span data-testid="sms-pricing-tab" className="flex items-center gap-2">
													<span className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white font-bold text-base border-2 border-blue-500 mr-2">
														3
													</span>
													SMS Pricing
												</span>
											}
											key="sms-pricing"
										>
											<div className="p-6 border-2 border-gray-300 rounded-2xl shadow-2xl bg-gray-50">
												<Title level={4}>SMS Pricing Configuration</Title>
												<Text className="block mb-4">
													Set your SMS pricing by zone. Each zone represents a group of countries
													with similar base costs. You can adjust the price you charge your
													customers for each zone in {selectedCurrency.toUpperCase()}.
												</Text>
												<SMSPricingConfig
													form={form}
													data={smsPricingData?.data}
													isLoading={isSmsPricingLoading}
													error={smsPricingError}
													currency={selectedCurrency}
													exchangeRates={exchangeRates}
												/>
											</div>
										</TabPane>
									</Tabs>

									<div className="flex justify-end mt-8">
										<Button
											type="primary"
											size="large"
											htmlType="submit"
											loading={createPlanMutation.isPending}
											disabled={isSmsPricingLoading || !!smsPricingError}
										>
											Create Plan
										</Button>
									</div>
								</Form>
							</Card>
						)}
					</>
				)}
			</div>

			<TutorialsModal
				isOpen={isTutorialsModalOpen}
				onClose={() => setIsTutorialsModalOpen(false)}
				tutorialItems={tutorialItems}
			>
				<TourButton onClick={handleTourClick} page="plan_creation" />
			</TutorialsModal>
			<ResellerTour tourSteps={planCreationTourSteps} />
		</ResellerDashboard>
	);
};

export default ResellerPlansCreation;
